import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LocationSearch } from "@/components/LocationSearch";
import { btnPrimary, inputCls } from "@/components/TopBar";

export const ManualLocationDialog = ({ vehicle, onClose, onSaved }) => {
  const [place, setPlace] = useState(
    vehicle.current_location
      ? {
          name: vehicle.current_location_name || `${vehicle.current_location.lat}, ${vehicle.current_location.lng}`,
          lat: vehicle.current_location.lat,
          lng: vehicle.current_location.lng,
        }
      : null
  );
  const [useCustom, setUseCustom] = useState(false);
  const [lat, setLat] = useState(vehicle.current_location?.lat ?? "");
  const [lng, setLng] = useState(vehicle.current_location?.lng ?? "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const body = useCustom
      ? { lat: Number(lat), lng: Number(lng), location_name: `${lat}, ${lng}` }
      : place && { lat: place.lat, lng: place.lng, location_name: place.name };
    if (!body || Number.isNaN(body.lat) || Number.isNaN(body.lng)) {
      toast.error("Search and select a place, or enter valid coordinates");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/vehicles/${vehicle.id}/location`, body);
      toast.success("Position updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1600] flex items-start justify-center overflow-y-auto p-4 py-10" data-testid="manual-location-dialog">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md border border-white/15 bg-[#0A0A0A] p-6 rise">
        <button
          type="button"
          onClick={onClose}
          data-testid="close-manual-location-btn"
          className="absolute right-3 top-3 border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <p className="overline">manual override</p>
        <h2 className="font-head text-2xl font-black tracking-tight">SET POSITION</h2>
        <p className="mt-2 text-[11px] text-[#A1A1AA]">
          {vehicle.plate_number} - use this when the tracker is offline. Distance and ETA recalculate instantly.
        </p>

        <div className="mt-6 space-y-4">
          <label className="flex items-center gap-2 text-[10px] tracking-[0.2em] text-[#A1A1AA]">
            <input
              type="checkbox"
              data-testid="manual-location-custom-toggle"
              checked={useCustom}
              onChange={(e) => setUseCustom(e.target.checked)}
              className="h-3 w-3 accent-[#00F0FF]"
            />
            ENTER RAW COORDINATES
          </label>

          {useCustom ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline mb-2 block">latitude</label>
                <input data-testid="manual-lat-input" value={lat} onChange={(e) => setLat(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="overline mb-2 block">longitude</label>
                <input data-testid="manual-lng-input" value={lng} onChange={(e) => setLng(e.target.value)} className={inputCls} />
              </div>
            </div>
          ) : (
            <LocationSearch label="location" value={place} onChange={setPlace} testid="manual-location" />
          )}
        </div>

        <button type="submit" disabled={busy} data-testid="submit-manual-location-btn" className={`${btnPrimary} mt-6 w-full py-3`}>
          {busy ? "saving" : "update position"}
        </button>
      </form>
    </div>
  );
};
